import React, { useEffect } from 'react'
import { AccessibleIcon } from '@radix-ui/react-accessible-icon'
import { forwardRef, styled, FloraVariants } from '@grupoboticario/flora-react-system'
import { Spinner } from '@grupoboticario/flora-react-spinner'
import { HTMLFloraProps } from '@grupoboticario/flora-react-utils'

// Não existe um token em design para essa cor. Por este motivo, foi criado um token temporário, que será substituído quando o design estiver pronto.
const actionNavigationWarningHighlight = '#C81414'

const StyledButton = styled('button', {
  display: 'inline-flex',
  pointerEvents: 'auto',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  appearance: 'none',
  userSelect: 'none',
  outline: 'none',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
  lineHeight: '16px',

  transition: `
    background 150ms ease,
      box-shadow 150ms ease-in-out,
      color 150ms ease
  `,

  '&:disabled, &[disabled], &[aria-disabled=true]': {
    background: '$disabledBackground',
    color: '$disabledContent',
    pointerEvents: 'none',
    cursor: 'auto',
  },

  '&::before, &::after': {
    boxSizing: 'border-box',
  },

  '&:active': {
    opacity: '0.8',
  },

  '&:focus-visible': {
    boxShadow: '$outline',
  },

  variants: {
    size: {
      small: {
        fontSize: '$bodySmall',
        padding: '$2 $3',
        height: 32,
      },
      standard: {
        fontSize: '$bodySmall',
        padding: '$4',
        height: 48,
      },
      large: {
        fontSize: '$bodyLarge',
        padding: '0 $6',
        height: 64,
      },
    },
    shape: {
      sharp: {
        borderRadius: 0,
      },
      small: {
        borderRadius: '$small',
      },
      medium: {
        borderRadius: '$medium',
      },
      large: {
        borderRadius: '$large',
      },
      pill: {
        borderRadius: '$pill',
      },
    },
    variant: {
      standard: {
        background: '$actionableDefault',
        color: '$actionableContent',
        '&:hover': {
          background: '$actionableHover',
        },
      },
      alt: {
        background: '$actionableAlt',
        color: '$actionableAltContent',
        '&:disabled': {
          background: '$disabledAltBackground',
          color: '$disabledAltContent',
        },
        '&:hover': {
          background: '$actionableAltHover',
        },
      },
      conversion: {
        background: '$conversionButtonDefault',
        color: '$conversionButtonContent',
        '&:hover': {
          background: '$conversionButtonHover',
        },
      },
      warning: {
        background: '$error-standard',
        color: '$actionableContent',
        '&:hover': {
          background: actionNavigationWarningHighlight,
        },
      },
      success: {
        background: '$success-standard',
        color: '$backgroundPrimary',
        '&:hover': {
          background: '$actionSuccessHighlight',
        },
      },
      alert: {
        background: '$alert-standard',
        color: '$actionableContent',
        '&:hover': {
          background: '$actionAlertHighlight',
        },
      },
      ghost: {
        background: 'transparent',
        boxShadow: 'inset 0 0 0 1px $colors$actionableDefault',
        color: '$actionableDefault',
        '&:hover': {
          background: '$actionableHover',
          boxShadow: 'inset 0 0 0 1px $colors$actionableHover',
          color: '$backgroundPrimary',
        },
        '&:disabled': {
          boxShadow: 'none',
        },
      },
      'ghost-alt': {
        background: 'transparent',
        boxShadow: 'inset 0 0 0 1px $colors$actionableAlt',
        color: '$actionableAlt',
        '&:hover': {
          background: '$actionableAlt',
          color: '$actionableHover',
        },
      },
      unstyled: {
        padding: 0,
        background: 'transparent',
        color: 'inherit',
      },
    },
    isFull: {
      true: {
        width: '100%',
      },
    },
    isUppercase: {
      true: {
        textTransform: 'uppercase',
      },
    },
    hasLeftIcon: {
      true: {
        paddingRight: '$5',
      },
    },
    hasRightIcon: {
      true: {
        paddingLeft: '$5',
      },
    },
  },
  defaultVariants: {
    variant: 'standard',
    size: 'standard',
    shape: 'small',
  },
})

/**
 * @deprecated Esta tipagem refere-se a um componente que será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-florabutton-guia-de-migra%C3%A7%C3%A3o--page| Acesse o guia de migração} para saber como migrar para a nova versão.
 */
export type ButtonProps = HTMLFloraProps<'button'> &
  FloraVariants<typeof StyledButton> & {
    /**
     * If added, the button will show an icon before the button's label.
     */
    leftIcon?: React.ReactElement

    /**
     * If added, the button will show an icon after the button's label.
     */
    rightIcon?: React.ReactElement

    /**
     * The accessible label for the icon. This label will be visually hidden but announced to screen
     * reader users, similar to `alt` text for `img` tags.
     */
    label?: string

    /**
     * If `true`, the button will show a spinner.
     */
    isLoading?: boolean

    /**
     * The label to show in the button when `isLoading` is true
     * If no text is passed, it only shows the spinner.
     */
    loadingText?: string
  }

const needMargin = (need: boolean) => (need ? 8 : 0)

/**
 * @deprecated Este componente será descontinuado em futuras Breaking Changes do Flora.
 * @tutorial
 * {@link https://florabook.gb.tech/v2/index.html?path=/docs/core-components-florabutton-guia-de-migra%C3%A7%C3%A3o--page| Acesse o guia de migração} para saber como migrar para a nova versão.
 */
export const Button = forwardRef<'button', ButtonProps>(
  (
    { children, leftIcon, rightIcon, label = '', disabled, isLoading, loadingText, ...props },
    forwardedRef,
  ) => {
    useEffect(() => {
      if ((leftIcon || rightIcon) && !children && !(label || props['aria-label'])) {
        throw new Error('you must use the label when passing an icon')
      }
    }, [leftIcon, rightIcon, children, label])

    const isDisabled = disabled || isLoading
    const spinnerSize = props.size === 'small' ? 'xxs' : 'xs'
    return (
      <StyledButton
        ref={forwardedRef}
        disabled={isDisabled}
        aria-disabled={isDisabled}
        data-flora="button"
        data-flora-text={typeof children === 'string' ? children : label}
        {...props}
        hasLeftIcon={!!leftIcon}
        hasRightIcon={!!rightIcon}
        css={{
          ...(props.css as any),
          'svg, [data-flora-spinner]': {
            marginLeft: !children ? 0 : needMargin(!!rightIcon),
            marginRight: !children ? 0 : needMargin(!!leftIcon || !!isLoading),
          },
        }}
      >
        {leftIcon && !isLoading && <AccessibleIcon label={label}>{leftIcon}</AccessibleIcon>}

        {isLoading ? (
          <>
            <Spinner size={spinnerSize} />
            {loadingText || children}
          </>
        ) : (
          children
        )}

        {rightIcon && !isLoading && <AccessibleIcon label={label}>{rightIcon}</AccessibleIcon>}
      </StyledButton>
    )
  },
)
